import type { AgencyCampaignSection } from '../../features/agency/model';
import type { ShigoDashboardContext } from '../../features/auth/dashboardDestination';
import type { NetworkAvailableFeature } from '../../features/auth/networkFeatures';
import type { ShigoPermission } from '../../features/auth/permissions';
import type { DashboardMessageKey } from '../../lib/messages';

export enum ShigoNavigationItem {
  Dashboard = 'dashboard',
  Debug = 'debug',
  Supply = 'supply',
  SupplyApps = 'supply-apps',
  SupplyDomains = 'supply-domains',
  SupplyTailoredSupply = 'supply-tailored-supply',
  CampaignAgency = 'campaign-agency',
  SupplyPublishers = 'supply-publishers',
  SupplyPlacements = 'supply-placements',
  SupplyLists = 'supply-lists',
  SupplyBlockedList = 'supply-blocked-list',
  SupplyEndpointGenerator = 'supply-endpoint-generator',
  SupplyAdsTxt = 'supply-ads-txt',
  Demand = 'demand',
  DemandOwners = 'demand-owners',
  DemandPartners = 'demand-partners',
  DemandIntegrations = 'demand-integrations',
  DemandDeals = 'demand-deals',
  Campaigns = 'campaigns',
  CampaignBrands = 'campaign-brands',
  CampaignSupply = 'campaign-supply',
  CampaignChannelGroups = 'campaign-channel-groups',
  CampaignCampaigns = 'campaign-campaigns',
  CampaignLineItems = 'campaign-line-items',
  CampaignCreatives = 'campaign-creatives',
  Targeting = 'targeting',
  TargetingBuilder = 'targeting-builder',
  Admin = 'admin',
  Networks = 'networks',
  AdminUsers = 'admin-users',
  AdminChangelog = 'admin-changelog',
  A2v = 'a2v',
  A2vAssets = 'a2v-assets',
  A2vNetworkTemplate = 'a2v-network-template',
  Reporting = 'reporting',
  ReportExplorer = 'report-explorer',
  PerformanceAnalyzer = 'performance-analyzer',
  NetworkPerformance = 'network-performance',
  SavedReports = 'saved-reports',
}

export type ShigoNavigationPath =
  | '/a/$agencyId/campaigns/$section'
  | '/a/$agencyId/reporting/report-explorer'
  | '/a/$agencyId/reporting/saved-reports'
  | '/debug'
  | '/supply/apps'
  | '/supply/apps/requests'
  | '/campaigns/agencies'
  | '/supply/publishers'
  | '/supply/placements'
  | '/supply/lists/all'
  | '/supply/domains'
  | '/supply/tailored-supply'
  | '/supply/blocked-list/creatives'
  | '/supply/endpoint-generator'
  | '/supply/ads-txt/records'
  | '/supply/ads-txt/apps'
  | '/supply/ads-txt/domains'
  | '/demand/owners'
  | '/demand/partners'
  | '/demand/integrations'
  | '/demand/deals'
  | '/campaigns/brands'
  | '/campaigns/supply/apps'
  | '/campaigns/channel-groups'
  | '/campaigns/campaigns'
  | '/campaigns/creatives'
  | '/targeting'
  | '/a2v/assets'
  | '/a2v/network-template'
  | '/networks'
  | '/admin/users'
  | '/admin/changelog'
  | '/reporting/report-explorer'
  | '/reporting/performance-analyzer'
  | '/reporting/network-performance'
  | '/reporting/saved-reports'
  | '/p/$publisherId/reporting/report-explorer';

export interface ShigoNavigationAccess {
  contexts?: readonly ShigoDashboardContext[];
  permissions?: readonly ShigoPermission[];
  networkFeatures?: readonly NetworkAvailableFeature[];
}

export interface ShigoNavigationEntry {
  section?: AgencyCampaignSection;
  id: ShigoNavigationItem;
  labelKey: DashboardMessageKey;
  to: ShigoNavigationPath | ShigoNavigationPathResolver;
  activePaths: readonly string[];
  access?: readonly ShigoNavigationAccess[];
}

export type ShigoNavigationPathResolver = (
  context: ShigoNavigationAccessContext,
) => ShigoNavigationPath;

export interface ShigoNavigationGroup {
  id: ShigoNavigationGroupItem;
  labelKey: DashboardMessageKey;
  activePaths: readonly string[];
  access?: readonly ShigoNavigationAccess[];
  entries: readonly ShigoNavigationEntry[];
}

export type ShigoNavigationGroupItem =
  | ShigoNavigationItem.Supply
  | ShigoNavigationItem.Demand
  | ShigoNavigationItem.Campaigns
  | ShigoNavigationItem.Targeting
  | ShigoNavigationItem.A2v
  | ShigoNavigationItem.Reporting
  | ShigoNavigationItem.Admin;

export interface ShigoNavigationAccessContext {
  dashboardContext: ShigoDashboardContext | null;
  can: (permission: ShigoPermission) => boolean;
  isFeatureAllowed: (feature: NetworkAvailableFeature) => boolean;
}

export interface VisibleShigoNavigationGroup extends ShigoNavigationGroup {
  entries: readonly VisibleShigoNavigationEntry[];
}

export interface VisibleShigoNavigationEntry extends Omit<ShigoNavigationEntry, 'to'> {
  to: ShigoNavigationPath;
}
